import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { type BoxSettingsFormFields } from "@modules/box/boxSettingsFormikType"
import { BoxPayoutFrequencySelector } from "@modules/box/components/boxSettingsForm/boxPayoutFrequencySelector"
import { BoxSettingsUploadDocument } from "@modules/box/components/boxSettingsForm/boxSettingsUploadDocument"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { PaymentMethodMode, PaymentMethodType } from "@modules/finances/types/financesType"
import { useMemo } from "react"

export const BoxSettingsForm = withForm({
  defaultValues: {} as BoxSettingsFormFields,
  props: {} as {
    className?: string
  } & React.ComponentProps<"div">,
  render: function BoxSettingsFormInner({ form, className, ...props }) {
    const { result: box } = useCurrentBox()

    const isBoxOfferLiteVersion = useMemo(
      () =>
        box &&
        (box.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
          box.stripeAccountStatus === StripeAccountStatus.ONBOARDING ||
          box.stripeAccountStatus === StripeAccountStatus.UNDEFINED)
          ? true
          : false,
      [box],
    )

    return (
      <div className={cn("flex w-full flex-col rounded-2xl bg-neutral-50 p-8 xl:w-8/12", className)} {...props}>
        <div className="mt-0 mb-5 flex flex-col">
          <Text fontStyle="HS">{word("box.form.settings.header")}</Text>
          <Text fontStyle="BM" className="text-neutral-800">
            {word("box.form.settings.subtitle")}
          </Text>
        </div>

        {/* PAYMENT METHOD SELECTION */}
        {!isBoxOfferLiteVersion && (
          <div className="my-4 flex flex-col">
            <Text fontStyle="TM">{word("box.form.paymentMethods.title")}</Text>
            <Text fontStyle="BM" className="mb-4 text-neutral-700" style={{ whiteSpace: "pre-line" }}>
              {word("box.form.paymentMethods.subtitle")}
            </Text>

            <form.AppField name="paymentMethodPreferences">
              {(field) => {
                const handleCheckboxChange = (type: PaymentMethodType, mode: PaymentMethodMode) => {
                  field.handleChange(
                    field.state.value.map((method) =>
                      method.type === type && method.mode === mode ? { ...method, enabled: !method.enabled } : method,
                    ),
                  )
                }

                return (
                  <>
                    <div className="flex flex-col gap-3 border-l border-neutral-300 py-2 pl-3">
                      {field.state.value
                        .filter((paymentMethod) => paymentMethod.mode === PaymentMethodMode.SUBSCRIPTION)
                        .map((paymentMethod, index) => (
                          <div key={index} className="flex flex-row items-center gap-2">
                            <Checkbox
                              key={paymentMethod.type}
                              id={paymentMethod.type}
                              value={paymentMethod.type}
                              checked={paymentMethod.enabled}
                              onCheckedChange={() => {
                                handleCheckboxChange(paymentMethod.type, paymentMethod.mode)
                              }}
                              className="size-5 cursor-pointer hover:border-purple-400 data-[state=checked]:border-transparent data-[state=checked]:bg-purple-500 data-[state=checked]:text-white data-[state=checked]:hover:border-purple-400"
                            />

                            <Text fontStyle="BM">
                              {paymentMethod.type === PaymentMethodType.CARD ||
                              paymentMethod.type === PaymentMethodType.SEPA_DEBIT
                                ? word(`box.form.paymentMethod.${paymentMethod.type}.label`)
                                : paymentMethod.type}
                            </Text>
                          </div>
                        ))}
                    </div>
                    <field.Message />
                  </>
                )
              }}
            </form.AppField>
          </div>
        )}

        {/* SLOT GENERATOR IN WEEKS */}
        <div className="my-4 flex flex-col">
          <Text fontStyle="TM">{word("box.form.slotGeneratorInWeeks.title")}</Text>
          <Text fontStyle="BM" className="mb-4 text-neutral-700">
            {word("box.form.slotGeneratorInWeeks.description")}
          </Text>

          <div className="border-l border-neutral-300 py-1 pl-3">
            <form.AppField name="slotGeneratorInWeeks">
              {(field) => (
                <field.Field>
                  <field.Label required>{word("box.form.slotGeneratorInWeeks.label")}</field.Label>
                  <field.Content>
                    <div className="flex flex-row items-center gap-2">
                      <field.NumberInput
                        className="w-20"
                        step={1}
                        min={2}
                        max={52}
                        onBeforeInput={(e) => {
                          if ("data" in e && isNaN(Number(e.data))) {
                            e.preventDefault()
                          }
                        }}
                      />
                      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition  */}
                      <Text fontStyle="TM">{word("global.week.label", { count: field.state.value ?? 1 })}</Text>
                    </div>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          </div>
        </div>

        {/* SHOW SESSION ATTENDEES */}
        <div className="my-4 flex flex-col">
          <Text fontStyle="TM">{word("box.form.showSessionAttendees.title")}</Text>
          <Text fontStyle="BM" className="mb-4 text-neutral-700">
            {word("box.form.showSessionAttendees.description")}
          </Text>

          <div className="border-l border-neutral-300 py-1 pl-3">
            <form.AppField name="showSessionAttendees">
              {(field) => <field.SwitchInline textSwitch={word("box.form.showSessionAttendees.label")} />}
            </form.AppField>
          </div>
        </div>

        {/* PAYOUT FREQUENCY */}
        {!isBoxOfferLiteVersion && <BoxPayoutFrequencySelector form={form} />}

        {/* AGREEMENT DOCUMENT */}
        <div className="my-4 flex flex-col">
          <Text fontStyle="TM">{word("box.form.agreementDocument.title")}</Text>
          <Text fontStyle="BM" className="mb-4 text-neutral-700">
            {word("box.form.agreementDocument.description")}
          </Text>

          <div className="border-l border-neutral-300 py-1 pl-3">
            <BoxSettingsUploadDocument />
          </div>
        </div>
      </div>
    )
  },
})
